import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAlert, mdiHistory, mdiSync } from '@mdi/js'
import { $api } from '../api/queryClient'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useFormatters } from '../lib/format'
import { SECTION_ICONS } from '../lib/section-icons'
import { runActorLabel, runSourceLabel } from '../lib/ontology-sync'
import { PageHeader } from './shared/PageHeader'
import { Button } from './ui/button'
import { Alert, AlertTitle } from './ui/alert'
import { AppIcon } from './ui/icon'
import { EmptyState } from './ui/empty-state'
import { Skeleton } from './ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'

const PAGE_SIZE = 20

/** The history of the organization's syncs, newest first (pattern 7). */
export function TypesSyncRunsPage() {
  const { org } = useParams({ from: '/$org/types/sync/runs/' })
  const { offset = 0 } = useSearch({ from: '/$org/types/sync/runs/' })
  const navigate = useNavigate({ from: '/$org/types/sync/runs/' })
  const { t } = useTranslation('types')
  const { formatDate } = useFormatters()

  useBreadcrumbs([
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb.types'), path: `/${org}/types` },
    { title: t('sync.breadcrumb'), path: `/${org}/types/sync` },
    { title: t('sync.history'), path: `/${org}/types/sync/runs` },
  ])

  const query = $api.useQuery('get', '/api/v1/organizations/{orgCode}/types/sync/runs', {
    params: { path: { orgCode: org }, query: { limit: PAGE_SIZE, offset } },
  })
  const runs = query.data?.data ?? []
  const pagination = query.data?.pagination

  return (
    <div className="space-y-4">
      <PageHeader
        data-testid="ontology-sync-runs-header"
        icon={SECTION_ICONS.types}
        title={t('sync.runs.title')}
        description={t('sync.runs.description')}
        actions={
          <Button asChild variant="outline" aria-label={t('sync.runs.back_to_sync')} title={t('sync.runs.back_to_sync')}>
            <Link to="/$org/types/sync" params={{ org }} data-testid="ontology-sync-runs-compare">
              <AppIcon path={mdiSync} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{t('sync.runs.back_to_sync')}</span>
            </Link>
          </Button>
        }
      />

      {query.isLoading && (
        <div className="space-y-2" data-testid="ontology-sync-runs-loading">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}

      {query.error && !query.isLoading && (
        <Alert variant="destructive" data-testid="ontology-sync-runs-error">
          <AppIcon path={mdiAlert} size="md" />
          <AlertTitle>{t('sync.load_error')}</AlertTitle>
        </Alert>
      )}

      {!query.isLoading && !query.error && runs.length === 0 && (
        <EmptyState
          variant="panel"
          icon={mdiHistory}
          title={t('sync.runs.empty_title')}
          description={t('sync.runs.empty_description')}
          data-testid="ontology-sync-runs-empty"
        />
      )}

      {runs.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <Table data-testid="ontology-sync-runs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('sync.runs.date')}</TableHead>
                  <TableHead>{t('sync.runs.source')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('sync.runs.user')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('sync.runs.families')}</TableHead>
                  <TableHead className="text-right">{t('sync.runs.created')}</TableHead>
                  <TableHead className="text-right">{t('sync.runs.updated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.uid} data-testid={`ontology-sync-run-row-${run.uid}`}>
                    <TableCell>
                      <Link
                        to="/$org/types/sync/runs/$runUid"
                        params={{ org, runUid: run.uid }}
                        className="text-primary hover:underline"
                        data-testid={`ontology-sync-run-link-${run.uid}`}
                      >
                        {formatDate(run.finished_at, { dateStyle: 'medium', timeStyle: 'short' })}
                      </Link>
                    </TableCell>
                    <TableCell>{runSourceLabel(run, t)}</TableCell>
                    <TableCell className="hidden md:table-cell">{runActorLabel(run, t)}</TableCell>
                    <TableCell className="hidden text-muted-foreground lg:table-cell">
                      {run.families.map((f) => t(`sync.families.${f}`)).join(', ')}
                    </TableCell>
                    <TableCell className="text-right">{run.counts.created}</TableCell>
                    <TableCell className="text-right">{run.counts.updated}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {pagination && pagination.total > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
              <span>
                {t('sync.runs.page', {
                  from: offset + 1,
                  to: Math.min(offset + runs.length, pagination.total),
                  total: pagination.total,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => navigate({ search: { offset: Math.max(0, offset - PAGE_SIZE) || undefined } })}
                data-testid="ontology-sync-runs-previous"
              >
                {t('sync.runs.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.has_more}
                onClick={() => navigate({ search: { offset: offset + PAGE_SIZE } })}
                data-testid="ontology-sync-runs-next"
              >
                {t('sync.runs.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
